import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

// ─────────────────────────────────────────────────────────────────────────────
// Global 401 → refresh → replay interceptor.
//
// Access tokens are short-lived. Without this, any panel that does
// `axios.put/post/delete` after the token expires (and doesn't have its own
// retry-on-401 wrapper) shows "Not authenticated" to the user. We install a
// single response interceptor that:
//   1. On 401, attempts `/api/auth/refresh` exactly once per original request.
//   2. If refresh succeeds, replays the original request and returns its result.
//   3. If refresh fails, propagates the 401 (so login screens / per-call
//      handlers can do their normal thing).
//
// Skips:
//   - Auth endpoints themselves (refresh/login/me) so we never loop.
//   - Requests that already carry our `_retry` marker.
// ─────────────────────────────────────────────────────────────────────────────
let _refreshInFlight = null;
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = String(original.url || '');

    const isAuthRoute =
      url.includes('/api/auth/refresh') ||
      url.includes('/api/auth/login') ||
      url.includes('/api/auth/logout') ||
      url.includes('/api/auth/me');

    if (status !== 401 || original._retry || isAuthRoute) {
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      // Coalesce concurrent refreshes — only one /refresh in flight at a time.
      if (!_refreshInFlight) {
        _refreshInFlight = axios.post(`${API_URL}/api/auth/refresh`).finally(() => {
          _refreshInFlight = null;
        });
      }
      await _refreshInFlight;
    } catch (_e) {
      return Promise.reject(error); // refresh failed — give up and propagate
    }
    // Replay the original request with the freshly-set cookie.
    return axios(original);
  },
);

// Error formatter for API responses
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check session on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      setUser(data);
      // AUTH_SPEC: set `ol_dev_session` if either the user is a developer
      // (semprion_admin) or the flag was already set (e.g. sign-in came
      // from the /dev page). Never CLEAR the flag from login — only the
      // "Full Sign Out" button on /dev does that.
      try {
        const isDev = !!data?.roles?.some(r => r.role_type === 'developer');
        if (isDev) localStorage.setItem('ol_dev_session', 'true');
      } catch {}
      return { success: true, user: data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    // AUTH_SPEC: new accounts should see a success message and then
    // sign in manually — never auto-log-in. The Login page handles the
    // toast + screen transition; here we just return the server
    // response WITHOUT calling setUser so the session stays empty.
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/register`, userData);
      return { success: true, user: data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async ({ fullSignOut = false } = {}) => {
    // Default behavior: if we're in a developer session, return to /dev.
    // `fullSignOut=true` (the "Full Sign Out" button on /dev) clears the
    // dev breadcrumb and returns to the primary /login screen.
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
      try {
        if (fullSignOut) localStorage.removeItem('ol_dev_session');
        localStorage.removeItem('ol.last_role');
      } catch {}
    }
  };

  const forgotPassword = async (email) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      return { success: true, message: data.message };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      return { success: true, message: data.message };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      return { success: true, message: data.message };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const refreshToken = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/refresh`);
      return true;
    } catch (error) {
      setUser(false);
      return false;
    }
  }, []);

  // Get user's primary role
  const getUserRole = () => {
    if (!user || !user.roles || user.roles.length === 0) return null;
    // A pinned active role wins (multi-role login).
    if (user.active_role) {
      return user.roles.find(r => r.role_type === user.active_role.role_type) || user.roles[0];
    }
    // Return highest priority role
    const rolePriority = ['semprion_admin', 'company_support', 'league_admin', 'ad', 'scheduler', 'coach', 'family'];
    for (const role of rolePriority) {
      const found = user.roles.find(r => r.role_type === role);
      if (found) return found;
    }
    return user.roles[0];
  };

  const hasRole = (roleType, orgId = null) => {
    if (!user || !user.roles) return false;
    return user.roles.some(r => {
      if (r.role_type !== roleType) return false;
      // Global roles never need an org match.
      if (r.role_type === 'semprion_admin' || r.role_type === 'company_support') return true;
      if (orgId && r.org_id !== orgId) return false;
      return true;
    });
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    refreshToken,
    getUserRole,
    hasRole,
    isAuthenticated: !!user,
    isAdmin: user && user.roles?.some(r => ['semprion_admin', 'company_support'].includes(r.role_type)),
    isSemprionAdmin: user && user.roles?.some(r => r.role_type === 'semprion_admin')
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
